import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete, Schedule } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const UrgencyFactorsTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    daysBeforeDeparture: 0,
    multiplier: 1.0,
    label: '',
    isActive: true,
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        daysBeforeDeparture: item.daysBeforeDeparture,
        multiplier: item.multiplier,
        label: item.label,
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      setFormData({
        daysBeforeDeparture: 0,
        multiplier: 1.0,
        label: '',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/urgency-factors/${editingItem.id}`,
          formData
        );
        onSuccess('Urgency factor updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/urgency-factors`, formData);
        onSuccess('Urgency factor created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this urgency factor?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/urgency-factors/${id}`);
        onSuccess('Urgency factor deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Urgency Factors Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Urgency Factor
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Days Before Departure</TableCell>
              <TableCell>Multiplier</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule fontSize="small" color="action" />
                    <Typography>{item.daysBeforeDeparture} days</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    {item.multiplier.toFixed(2)}×
                  </Typography>
                </TableCell>
                <TableCell>{item.label}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Urgency Factor' : 'Add Urgency Factor'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Days Before Departure"
              type="number"
              value={formData.daysBeforeDeparture}
              onChange={(e) =>
                setFormData({ ...formData, daysBeforeDeparture: parseInt(e.target.value) })
              }
              inputProps={{ min: 0 }}
              fullWidth
              helperText="Number of days before departure when this multiplier applies"
            />
            <TextField
              label="Multiplier"
              type="number"
              value={formData.multiplier}
              onChange={(e) =>
                setFormData({ ...formData, multiplier: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              helperText="Price multiplier (e.g., 1.50 = 50% increase)"
            />
            <TextField
              label="Label"
              value={formData.label}
              onChange={(e) =>
                setFormData({ ...formData, label: e.target.value })
              }
              fullWidth
              helperText="Descriptive label (e.g., 'Last Minute', 'Early Bird')"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UrgencyFactorsTab;
